import React from 'react';
import { Button } from '@components/atoms/Button';
import { Task } from '@models/task.model';
import { SelectOption } from '@components/atoms/ReactSelect';
import { SubtasksTable } from './SubtasksTable';
import { useTranslation } from '@hooks/useTranslation';

interface SubtasksSectionProps {
  task?: Task | null;
  existingSubtasks: Task[];
  subtasksToAdd: string[];
  subtasksToRemove: string[];
  availableTasksForSubtasks: SelectOption[];
  allTasks: Task[];
  hasActiveBudgetRequest: boolean;
  onAddSubtaskClick: () => void;
  onRemoveSubtask: (taskId: string) => void;
  onCreateSubtaskClick?: () => void;
  onImportTemplateClick?: () => void;
}

export const SubtasksSection: React.FC<SubtasksSectionProps> = ({
  task,
  existingSubtasks,
  subtasksToAdd,
  subtasksToRemove,
  availableTasksForSubtasks,
  allTasks,
  hasActiveBudgetRequest,
  onAddSubtaskClick,
  onRemoveSubtask,
  onCreateSubtaskClick,
  onImportTemplateClick,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">{tTaskDetail.general.subtasksTitle}</h3>
        <div className="flex gap-2">
          {/* Create Subtask button - only show for existing tasks */}
          {task?.taskId && onCreateSubtaskClick && (
            <Button
              type="button"
              onClick={onCreateSubtaskClick}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={
                !!task?.delegateOrganizationId ||
                hasActiveBudgetRequest
              }>
              {tTaskDetail.general.createSubtask}
            </Button>
          )}

          {/* Import Template button - only show for existing tasks */}
          {task?.taskId && onImportTemplateClick && (
            <Button
              type="button"
              onClick={onImportTemplateClick}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={
                !!task?.delegateOrganizationId ||
                hasActiveBudgetRequest
              }>
              {tTaskDetail.general.importTemplate}
            </Button>
          )}

          <Button
            type="button"
            onClick={onAddSubtaskClick}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={
              !task?.taskId ||
              availableTasksForSubtasks.length === 0 ||
              !!task?.delegateOrganizationId ||
              hasActiveBudgetRequest
            }>
            {tTaskDetail.general.addSubtasksButton}
          </Button>
        </div>
      </div>

      {/* Alert for delegated tasks */}
      {task?.delegateOrganizationId && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                <strong>{tTaskDetail.general.infoLabel}</strong>{' '}
                {tTaskDetail.general.delegatedNoSubtasksMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alert for active budget request */}
      {hasActiveBudgetRequest && !task?.delegateOrganizationId && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                <strong>{tTaskDetail.general.infoLabel}</strong>{' '}
                {tTaskDetail.general.pendingBudgetNoSubtasksMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      <SubtasksTable
        existingSubtasks={existingSubtasks}
        subtasksToAdd={subtasksToAdd}
        subtasksToRemove={subtasksToRemove}
        onRemoveSubtask={onRemoveSubtask}
        task={task}
        availableTasksCount={availableTasksForSubtasks.length}
        allTasks={allTasks}
      />
    </div>
  );
};